import React, { useMemo, useState } from 'react';

import { FilteredTable, Link } from '@common/components';
import Text from 'antd/lib/typography/Text';
import { Colors } from '@common/tokens';
import { SorterResult } from 'antd/lib/table/interface';
import { SpearfishingCampaignQueryCgResponse } from '@common/graphql/schema';

type StateType = 'sent' | 'claimed' | 'declined' | 'outdated';

const statusColor = {
  declined: Colors.coral,
  claimed: Colors.meadowDarker,
};

type Campaign =
  SpearfishingCampaignQueryCgResponse['spearfishingCampaign']['campaign'];

type SpearfishingNotification = NonNullable<
  Campaign['spearfishingNotifications']
>[number];

export function Recipients({
  spearfishingCampaign,
}: {
  spearfishingCampaign:
    | SpearfishingCampaignQueryCgResponse['spearfishingCampaign']
    | undefined;
}) {
  const [sortedData, setSortedData] = useState<SpearfishingNotification[]>([]);

  const sortByName = (
    a: SpearfishingNotification,
    b: SpearfishingNotification,
    order: 'ascend' | 'descend'
  ) => {
    const aName = a.targetWorksMembership?.worksUser?.name;
    const bName = b.targetWorksMembership?.worksUser?.name;
    const orderNumber = order === 'ascend' ? 1 : -1;
    if (aName < bName) {
      return -orderNumber;
    }
    if (aName > bName) {
      return orderNumber;
    }
    return 0;
  };

  const sortByState = (
    a: SpearfishingNotification,
    b: SpearfishingNotification,
    order: 'ascend' | 'descend'
  ) => {
    const aState = a.state;
    const bState = b.state;
    const orderNumber = order === 'ascend' ? 1 : -1;
    if (aState < bState) {
      return -orderNumber;
    }
    if (aState > bState) {
      return orderNumber;
    }
    return 0;
  };

  const sortArray = (sorter: SorterResult<SpearfishingNotification>) => {
    const data = [...recipients];

    let sorted: SpearfishingNotification[] = [];
    if (sorter.columnKey === 'name') {
      sorted = data.sort((a, b) => sortByName(a, b, sorter.order!));
    }
    if (sorter.columnKey === 'state') {
      sorted = data.sort((a, b) => sortByState(a, b, sorter.order!));
    }
    setSortedData(sorted);
  };

  const campaign = spearfishingCampaign?.campaign;

  const recipients = useMemo(
    () => campaign?.spearfishingNotifications || [],
    [campaign?.spearfishingNotifications]
  );

  const initialSortedRecipients = useMemo(() => {
    const data = [...recipients];
    return data.sort((a, b) => sortByState(a, b, 'ascend'));
  }, [recipients]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'targetWorksMembership',
      key: 'name',
      render: (
        membership: { worksUser: { name: string } },
        record: { targetWorker: { id: string } }
      ) => {
        return (
          <Link
            to={`/works/team/on_demand/${record.targetWorker.id}`}
            className="text-book"
          >
            {membership?.worksUser?.name}
          </Link>
        );
      },
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      render: (state: StateType) => {
        return (
          <Text
            style={{
              textTransform: 'capitalize',
              color:
                statusColor?.[state as keyof typeof statusColor] ||
                Colors.textDefault,
            }}
          >
            {state}
          </Text>
        );
      },
      sorter: true,
    },
  ];

  const handleTableChange = (
    _pagination: { current: number; pageSize: number },
    _newFilters: { [key: string]: unknown },
    sorter: SorterResult<SpearfishingNotification>
  ) => {
    if (sorter?.order && sorter?.columnKey) {
      sortArray(sorter);
    } else {
      setSortedData([]);
    }
  };

  return (
    <>
      <FilteredTable
        className="ShiftMessagesRecipients"
        columns={columns}
        rowKey="id"
        dataSource={sortedData?.length ? sortedData : initialSortedRecipients}
        pagination={{
          hideOnSinglePage: true,
          pageSize: recipients?.length,
          total: recipients?.length,
        }}
        scroll={{ x: '100%' }}
        filters={{}}
        loading={false}
        size="middle"
        localStorageFilterKey="ShiftMessagesRecipients"
        onFilterChange={() => ({})}
        onChange={handleTableChange}
      />
      <style jsx>{`
        :global(
          .ShiftMessagesRecipients
            .ant-table.ant-table-middle
            .ant-table-tbody
            > tr
            > td
        ) {
          padding: 18px 4px;
        }
      `}</style>
    </>
  );
}
